'use client';

import appLogger from '@/utils/logger';
import { scrubHubSpotInstallSecretsFromUrl } from '@/lib/hubspot-marketplace-install';
import { isSalesforceEmbedFrame, scrubCanvasHandoffFromUrl } from '@/utils/salesforceEmbed';
import { scrubAuthTokensFromUrl } from '@/utils/authUrlScrub';
import { isBenignHydrationMismatchNoise } from '@/lib/observability/hydration-mismatch-noise';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import type { Site } from '@datadog/browser-core';
import { useEffect, useRef } from 'react';
import { getPublicApiBaseUrl } from '@/utils/publicApiBaseUrl';

/**
 * Standardize environment tags to short form (prod/dev)
 * This ensures consistency across all Datadog logs/traces/metrics
 */
function normalizeEnv(env?: string): string {
  const normalized = (env || 'development').toLowerCase();
  if (normalized === 'production') return 'prod';
  if (normalized === 'development') return 'dev';
  return normalized; // Keep other values as-is (staging, test, etc.)
}

/**
 * Datadog RUM (Real User Monitoring) Initialization Component
 *
 * This component initializes Datadog RUM and Logs for frontend monitoring.
 * It must be rendered in the root layout to ensure early error capture.
 *
 * Features:
 * - Real User Monitoring (RUM) for performance and errors
 * - Browser Logs for console errors and custom logs
 * - Distributed tracing to correlate frontend requests with backend APM
 * - Session replay for debugging user issues
 * - Global error handlers for uncaught errors and promise rejections
 *
 * Environment Variables Required:
 * - NEXT_PUBLIC_DATADOG_APPLICATION_ID
 * - NEXT_PUBLIC_DATADOG_CLIENT_TOKEN
 * - NEXT_PUBLIC_DATADOG_SITE (default: datadoghq.com)
 * - NEXT_PUBLIC_DATADOG_ENV (default: development, normalized to prod/dev)
 * - NEXT_PUBLIC_DATADOG_SERVICE (default: mb-frontend)
 * - NEXT_PUBLIC_DATADOG_VERSION (default: 0.1.0)
 */
export default function DatadogInit() {
  const initializedRef = useRef(false);

  useEffect(() => {
    // Only initialize once and only in browser
    if (typeof window === 'undefined') {
      return;
    }

    // Skip Datadog initialization in local development
    const hostUrl = process.env.NEXT_PUBLIC_HOST_URL || '';
    const isLocalhost = hostUrl.includes('localhost') || hostUrl.includes('127.0.0.1') || hostUrl === '';
    if (isLocalhost) {
      appLogger.info('Datadog RUM: Skipping initialization in local development');
      return;
    }

    // CRITICAL: Check if Datadog is already initialized globally
    // This prevents the "SDK loaded more than once" error
    if (((window as unknown as { DD_RUM?: { isInitialized?: boolean } }).DD_RUM?.isInitialized) || initializedRef.current) {
      appLogger.info('Datadog RUM: Already initialized, skipping re-initialization');
      return;
    }

    // Check if required environment variables are present
    const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
    const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;

    if (!applicationId || !clientToken) {
      appLogger.warn('Datadog RUM: Missing required environment variables (APPLICATION_ID or CLIENT_TOKEN). Skipping initialization.');
      return;
    }

    initializedRef.current = true;

    const datadogSite = (process.env.NEXT_PUBLIC_DATADOG_SITE as Site | undefined) ?? 'datadoghq.com';
    const normalizedEnv = normalizeEnv(process.env.NEXT_PUBLIC_DATADOG_ENV);

    const resolvedApiBase = getPublicApiBaseUrl();
    let apiTracingMatch = process.env.NEXT_PUBLIC_API_ENDPOINT || 'http://localhost:8881';
    try {
      if (resolvedApiBase) {
        apiTracingMatch = new URL(
          resolvedApiBase.startsWith('http') ? resolvedApiBase : `http://${resolvedApiBase}`,
        ).origin;
      }
    } catch {
      // keep apiTracingMatch fallback
    }

    try {
      // Initialize RUM (Real User Monitoring)
      datadogRum.init({
        // Required fields
        applicationId: applicationId,
        clientToken: clientToken,
        site: datadogSite,

        // Service identification (matches backend pattern)
        service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || 'mb-frontend',
        env: normalizedEnv,
        version: process.env.NEXT_PUBLIC_DATADOG_VERSION || '0.1.0',

        // Prevent errors if initialization is attempted multiple times
        silentMultipleInit: true,

        // Always granted: RUM/Logs here is strictly-necessary operational and error
        // monitoring, not marketing/advertising analytics -- distinct from the
        // consent-gated "business events" layer (lib/analytics/track.ts's
        // datadogRum.addAction calls), which still checks hasAnalyticsConsent().
        // This SDK-level flag previously mirrored hasAnalyticsConsent(), but the only
        // UI that ever grants it (CookieConsentBanner) is mounted exclusively on the
        // public marketing site (app/(public)/layout.tsx) -- a different origin from
        // the authenticated app, so authenticated users could never satisfy it and RUM
        // transport was permanently blocked for all real product usage.
        trackingConsent: 'granted',

        // Sampling rates - 100% MONITORING (HARDCODED)
        sessionSampleRate: 100, // 100% of sessions tracked
        sessionReplaySampleRate: 100, // 100% of sessions include replay - FULL MONITORING

        // Performance tracking
        trackUserInteractions: true,    // Track clicks, form submissions, page navigation
        trackResources: true,           // Track XHR, fetch, images, scripts, CSS
        trackLongTasks: true,           // Track tasks >50ms that block main thread

        // Privacy: session replay never captures what people type into fields. Recording
        // keystrokes on the public site (e.g. the demo form) to a third party is the
        // pattern California wiretap (CIPA) suits target; clicks, navigation and layout
        // are kept. The website chat runs in a chat.marketbetter.ai iframe this recorder
        // can't see into, and that app has no session replay.
        defaultPrivacyLevel: 'mask-user-input',

        // Session storage strategy (environment-aware)
        // Development (localhost/HTTP): Use localStorage (secure cookies don't work on HTTP)
        // Production (HTTPS): Use secure cookies
        sessionPersistence: normalizedEnv === 'prod' ? 'cookie' : 'local-storage',

        // Security (only for production with HTTPS)
        trackSessionAcrossSubdomains: normalizedEnv === 'prod',
        useSecureSessionCookie: normalizedEnv === 'prod',

        // Distributed tracing - connect frontend requests to backend APM traces
        allowedTracingUrls: [
          // Local development (includes LAN IP when API host follows page host)
          {
            match: apiTracingMatch,
            propagatorTypes: ['datadog']
          },
          // Dev server
          {
            match: 'https://dev.marketbetter.ai',
            propagatorTypes: ['datadog']
          },
          // Production
          {
            match: 'https://marketbetter.ai',
            propagatorTypes: ['datadog']
          },
          // Azure Web PubSub (Socket.IO)
          {
            match: process.env.NEXT_PUBLIC_SOCKET_URL || 'https://mbdev.webpubsub.azure.com',
            propagatorTypes: ['datadog']
          }
        ],

        // Before send hook - enrich events with custom attributes
        beforeSend: (event) => {
          // The HubSpot Marketplace install page carries a live OAuth `code` and
          // `state` in its query string at step=finalize. Views are auto-tracked
          // (trackViewsManually: false), so redact before anything leaves the browser.
          // Same for the Salesforce Canvas handoff code (/salesforce-account#canvasCode=…):
          // a single-use credential until redeemed, so it never leaves the browser.
          // Same for the password-reset token_hash (/reset-password?token_hash=…).
          if (event.view?.url) {
            event.view.url = scrubAuthTokensFromUrl(scrubCanvasHandoffFromUrl(scrubHubSpotInstallSecretsFromUrl(event.view.url)));
          }
          // The initial-document resource event's URL comes from the navigation
          // PerformanceEntry — history.replaceState cannot change it, so it must
          // be scrubbed here too ('resource.url' is beforeSend-modifiable).
          if (event.type === 'resource' && event.resource?.url) {
            event.resource.url = scrubAuthTokensFromUrl(scrubCanvasHandoffFromUrl(scrubHubSpotInstallSecretsFromUrl(event.resource.url)));
          }

          // Drop CSP violation reports whose cited policy isn't ours. Some browser
          // extensions / corporate proxies / local security tools inject their own
          // CSP (e.g. `connect-src 'none'; report-uri https://csp-reporting.cloud...`)
          // which then reports every fetch as a violation. Our real policy never
          // has `connect-src 'none'` and never uses `report-uri`.
          if (event.type === 'error') {
            const err = (event as { error?: { csp?: unknown; stack?: string; message?: string } }).error;
            const stack = err?.stack ?? '';
            if (err?.csp && (stack.includes("connect-src 'none'") || stack.includes('report-uri'))) {
              return false;
            }
            if (isBenignHydrationMismatchNoise(err?.message)) {
              return false;
            }
          }

          if (typeof window !== 'undefined') {
            const workspaceId = localStorage.getItem('activeWorkspace');
            if (workspaceId && workspaceId !== 'null' && workspaceId !== 'undefined') {
              event.context = event.context || {};
              event.context.workspace_id = workspaceId;
            }
          }

          return true;
        },

        // Enable action tracking
        trackViewsManually: false, // Automatically track views

        // Note: reactPlugin is for React Router, not Next.js App Router
        // Next.js App Router is tracked automatically via trackViewsManually: false
        // Removed: plugins: [reactPlugin({ router: true })]
      });

      // Initialize Logs (for console errors and custom logging)
      datadogLogs.init({
        clientToken: clientToken,
        site: datadogSite,
        service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || 'mb-frontend',
        env: normalizedEnv,
        version: process.env.NEXT_PUBLIC_DATADOG_VERSION || '0.1.0',

        // Session storage strategy (must match RUM configuration)
        sessionPersistence: normalizedEnv === 'prod' ? 'cookie' : 'local-storage',

        // Forward browser console errors to Datadog
        forwardErrorsToLogs: true,

        // Forward ALL console methods - FULL MONITORING
        forwardConsoleLogs: ['log', 'info', 'warn', 'error', 'debug'], // All console methods

        // Forward browser reports. 'csp_violation' is intentionally EXCLUDED:
        // the dominant source of CSP-violation reports in the wild is third-party
        // software (browser extensions, corporate proxies, endpoint-security tools)
        // that inject their OWN restrictive policy — e.g.
        //   script-src 'unsafe-inline' 'unsafe-eval'; connect-src 'none'; report-uri /api/csp-report
        // Our served policy never uses 'unsafe-inline'/'unsafe-eval' in script-src,
        // never 'connect-src none', and has NO report-uri (verified: proxy.ts). Those
        // injected-policy violations are unactionable by us yet were leaking into
        // Datadog via this logs channel (the RUM beforeSend filter below only covers
        // the RUM channel), masquerading as a regression. Genuine violations of OUR
        // policy still surface as RUM errors. (Finding 1, 2026-06-18.)
        forwardReports: ['intervention', 'deprecation'],

        // Sampling - 100% MONITORING (HARDCODED)
        sessionSampleRate: 100,

        // Every log event is stamped with view.url = location.href at creation
        // time — including logs emitted before the install page strips the OAuth
        // code/state from the address bar. The RUM beforeSend above does NOT
        // cover this channel (see forwardReports note), so scrub it here too.
        beforeSend: (event) => {
          if (event.view?.url) {
            event.view.url = scrubAuthTokensFromUrl(scrubCanvasHandoffFromUrl(scrubHubSpotInstallSecretsFromUrl(event.view.url)));
          }
          // forwardErrorsToLogs turns console.error calls (including React's own
          // hydration-mismatch reporting) into logs on this channel. See
          // lib/observability/hydration-mismatch-noise.ts for why these are
          // dropped rather than fixed in app code.
          if (isBenignHydrationMismatchNoise(event.message)) {
            return false;
          }
          return true;
        },
      });

      // Add global error handlers for uncaught errors
      window.onerror = (message, source, lineno, colno, error) => {
        datadogRum.addError(error || new Error(String(message)), {
          source: 'window.onerror',
          line: lineno,
          column: colno,
          file: source,
        });
        // Return false to let default error handling continue
        return false;
      };

      // Add handler for unhandled promise rejections
      let reloadScheduled = false;

      window.onunhandledrejection = (event) => {
        const errorMessage = event.reason?.message || event.reason?.toString() || '';

        // Detect Server Action mismatch errors and trigger page reload
        // These occur when client has stale JS bundles after a deployment
        if (
          errorMessage.includes('Failed to find Server Action') ||
          errorMessage.includes('ChunkLoadError')
        ) {
          // Log only the first occurrence — subsequent errors in the same burst
          // are the same root cause and just add noise
          if (!reloadScheduled) {
            // A Salesforce embed cannot sign itself back in after a reload; leave it be.
            const salesforceEmbed = isSalesforceEmbedFrame();
            appLogger.warn(
              salesforceEmbed
                ? 'Server Action or chunk mismatch detected in a Salesforce embed, not reloading'
                : 'Server Action or chunk mismatch detected, reloading page',
              { errorMessage, source: 'unhandledrejection' },
            );

            datadogRum.addError(event.reason, {
              source: 'unhandledrejection',
              type: 'server_action_mismatch',
              action: salesforceEmbed ? 'none_salesforce_embed' : 'auto_reload',
            });

            reloadScheduled = true;
            if (salesforceEmbed) return;

            // Guard against reload loops across page loads using shared counter
            try {
              const RELOAD_KEY = 'mb_error_reload_count';
              const MAX_RELOADS = 2;
              const count = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
              if (count >= MAX_RELOADS) return;
              sessionStorage.setItem(RELOAD_KEY, String(count + 1));
            } catch {
              // sessionStorage unavailable — proceed with single reload
            }

            window.location.reload();
          }
          return;
        }

        datadogRum.addError(event.reason, {
          source: 'unhandledrejection',
          promise: event.promise?.toString() || 'unknown',
        });
      };

      // Explicitly start session replay recording
      // This ensures RUM starts tracking immediately
      datadogRum.startSessionReplayRecording();

      // NOTE: user identification (id/name/email) is intentionally NOT set here.
      // It used to read localStorage.user and call datadogRum.setUser() unconditionally,
      // which stamped PII onto the RUM session BEFORE — and regardless of — cookie consent
      // (trackingConsent gates transport, not the local session record). Identity is now
      // owned solely by AnalyticsProvider's consent-gated identify path (identifyUser →
      // datadogRum.setUser), so PII is only attached once the visitor has accepted.

      appLogger.info('✅ Datadog RUM initialized successfully', {
                service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || 'mb-frontend',
                env: normalizedEnv,
                version: process.env.NEXT_PUBLIC_DATADOG_VERSION || '0.1.0',
              });

    } catch (error) {
      appLogger.error('❌ Failed to initialize Datadog RUM:', { data: error });
    }
  }, []);

  // This component doesn't render anything
  return null;
}
